'use client';

/* eslint-disable @next/next/no-img-element -- favicon/external/data-URI; next/image migration impractical for these dynamic logo/signature sources */
import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Check,
  ChevronsUpDown,
  ChevronDown,
  Plus,
  Edit2,
  ExternalLink,
  AlertCircle,
  X,
  Loader2,
  Trash2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { DeleteConfirmationDialog } from '@/components/ui/delete-confirmation-dialog';
import {
  ATTRIBUTE_AFFORDANCE,
  ATTRIBUTE_AFFORDANCE_REVEALED,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_EDITOR_CHROME,
  ATTRIBUTE_HOVER,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_NOT_RECORDED,
  ATTRIBUTE_POPOVER_W_WIDE,
  ATTRIBUTE_RADIUS,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_TEXT,
  attributeLabelWidthStyle
} from '@/components/attributes';
import {
  updateInstitutionAction,
  deleteInstitutionAction
} from '@/lib/services/institutions/actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AttributeRow } from './attribute-row';
import {
  describeInstitutionRow,
  formatInstitutionDistanceKm
} from './institution-descriptor';
import {
  tierInstitutionResults,
  initialInstitutionSortMode,
  INSTITUTION_SORT_STORAGE_KEY,
  type InstitutionSortMode
} from './institution-result-tiers';
import { InlineCreateStep } from './inline-create-step';
import {
  DEFAULT_NEW_INSTITUTION_TYPE,
  type InstitutionTypeSide
} from './institution-type-options';
import { InstitutionTypeSelect } from './institution-type-select';
import { NearDuplicateStep } from './near-duplicate-step';
import { getInstitutionDuplicateCandidatesAction } from '@/lib/services/institutions/actions';
import type { InstitutionDuplicateCandidate } from '@/lib/services/institutions/types';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';

/**
 * How many institutions the picker offers before anything is typed (VALE-1031).
 * A shortlist, not a catalogue — the point is to make the common pick a single
 * click, and a longer list stops being scannable.
 */
const DEFAULT_SHORTLIST_SIZE = 10;

/**
 * Institution type from backend
 */
export interface Institution {
  id: number;
  name: string;
  type: string;
  faviconUrl?: string | null;
  email?: string;
  isCustom?: boolean;

  /*
    **Four search-only fields the backend attaches when `q` is present** (VALE-1423).
    All optional: a browsing listing carries none of them, and the picker falls back to
    the single ungrouped list it has always rendered.
  */

  /** Which tier this row came back in — see `institution-result-tiers.ts`. */
  matchTier?: 'contains' | 'fuzzy';
  /**
   * Whether this fuzzy row is in the category the asset implies (VALE-1636). Set only on
   * a Best-match search made in a specific asset-type context, so the picker can demote
   * out-of-category fuzzy rows into a collapsed "Other institution types" tier. Absent on
   * a browse, a proximity search, a type-less search, and on every `contains` row.
   */
  typeScope?: 'in-category' | 'other-type';
  /** The backend's folded identity key for `name`; what the A–Z sort orders on. */
  sortName?: string;
  /** Active institution forms Vale holds, for the second line. */
  formCount?: number;
  /** When this row was added, for a firm-owned row's provenance line. */
  createdAt?: string;
  /**
   * Distance from the proximity anchor in km (VALE-1596). Set only in proximity mode
   * (search or the no-query nearest browse); `null` on a coordinate-less row a proximity
   * search still returns; absent on every non-proximity listing.
   */
  distanceKm?: number | null;
}

/**
 * Custom institution (not yet created in backend)
 * Used during matter creation when user can't find existing institution
 */
export interface CustomInstitution {
  id: -1; // Special ID to indicate custom institution
  name: string;
  email: string;
  type: 'custom';
  isCustom: true;
  /**
   * Existing institution ids the reader was shown as looking like the same place, and
   * chose to create this one alongside anyway (VALE-1422).
   *
   * Carried on the placeholder rather than acted on here, because the institution is
   * not created by the picker — it is created by the inventory-item write that
   * receives `institutionName`/`institutionEmail`. So the reader's answer has to
   * travel with the placeholder to the surface that actually performs the create.
   */
  acknowledgedDuplicateInstitutionIds?: number[];
  /**
   * The institution type the reader picked on the create step (VALE-1577).
   *
   * Only set where the step offers the choice — the `Other` / "Organisation"
   * picker. Everywhere else it is absent, and the backend keeps deriving the type
   * from the inventory item type, which is already exact there (a Bank Account
   * drawer can only be minting a bank).
   *
   * Carried on the placeholder for the same reason as
   * {@link CustomInstitution.acknowledgedDuplicateInstitutionIds}: the institution
   * is not created by the picker but by the inventory-item write downstream, so
   * the reader's choice has to travel with the placeholder to reach it.
   */
  institutionType?: string;
}

/**
 * Value can be Institution object, CustomInstitution, or string (legacy format)
 */
export type InstitutionValue =
  | Institution
  | CustomInstitution
  | string
  | null
  | undefined;

interface InstitutionSearchFieldProps {
  /** Current institution value (object or string) */
  value?: InstitutionValue;

  /** Callback when institution changes */
  onChange: (
    institution: Institution | CustomInstitution | string | null
  ) => void;

  /** Backend search callback */
  onInstitutionSearch?: (searchQuery: string) => void;

  /** Callback to view institution details */
  onViewInstitution?: (institution: Institution) => void;

  /** Institutions list from backend */
  institutions: Institution[];

  /** Loading state for institutions */
  loadingInstitutions: boolean;

  /** Display mode */
  mode: 'read' | 'edit';

  /** Label for the institution type (e.g., "Bank", "Insurance Company") */
  institutionTypeName?: string;

  /** Whether field is disabled */
  disabled?: boolean;

  /** Whether field is required */
  required?: boolean;

  /** Validation error message */
  error?: string;

  /**
   * Render without the field's own `{institutionTypeName}` label (LF-6314).
   *
   * For a host that already names the section the field opens — the attribute
   * drawer puts an `ACCOUNT` / `FACILITY` eyebrow directly above it — where the
   * component's own label repeats the heading a row later. Defaults to `false`,
   * so every existing consumer is unchanged.
   */
  hideLabel?: boolean;

  /**
   * Which chrome to wear (LF-6314). Defaults to `form`, so all 18 consumers are
   * unchanged.
   *
   * `compact` puts the field on the attribute treatment: the trigger and the
   * selected-institution card wear the row's editor chrome and its type instead of
   * a `Card` with its own border and `text-sm`/`font-medium` scale. **The popover,
   * the search, the create step and every behaviour are identical** — this is ink
   * and geometry only, which is why it is a variant rather than a second component.
   */
  variant?: 'form' | 'compact';

  /**
   * Offer an institution-type choice on the create step, scoped to the given side
   * (VALE-1577, scoping added VALE-1595).
   *
   * **Opt-in, and only the `Other` / "Organisation" picker opts in.** Every other
   * picker is already type-specific — a Bank Account drawer's create can only be
   * minting a bank — so a selector there would ask a question whose answer the
   * surface already knows, and offer the reader a way to get it wrong. The
   * `Other` picker is the one place the type is genuinely unknown: today every
   * organisation minted there is filed as `other` whatever it actually is.
   *
   * **The value carries which side of the asset/liability split this picker
   * mints for.** The offered types are scoped to it — an asset picker must not
   * offer a liability-only institution type (Lender, Tax Authority, Funeral
   * Service, Utility Provider, Professional Service), and a liability picker
   * must not offer an asset-only one — so the caller states its side rather than
   * this field guessing it.
   *
   * Defaults to `false`, so all other consumers render unchanged.
   */
  allowTypeSelection?: InstitutionTypeSide | false;
}

/**
 * Type guard to check if institution is a pre-save custom institution (id: -1)
 */
function isCustomInstitution(
  inst: Institution | CustomInstitution | null
): inst is CustomInstitution {
  return (
    inst !== null &&
    inst.id === -1 &&
    'isCustom' in inst &&
    inst.isCustom === true
  );
}

/**
 * Type guard to check if institution is a saved custom institution (id > 0, isCustom: true)
 */
function isSavedCustomInstitution(
  inst: Institution | CustomInstitution | null
): inst is Institution {
  return (
    inst !== null && inst.id > 0 && 'isCustom' in inst && inst.isCustom === true
  );
}

/**
 * Institution search field component with clear, edit custom, and edit saved email support
 */
export function InstitutionSearchField({
  value,
  onChange,
  onInstitutionSearch,
  onViewInstitution,
  institutions,
  loadingInstitutions,
  mode,
  institutionTypeName = 'Institution',
  disabled = false,
  required = false,
  error,
  hideLabel = false,
  variant = 'form',
  allowTypeSelection = false
}: InstitutionSearchFieldProps) {
  const compact = variant === 'compact';
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [showChangeInstitution, setShowChangeInstitution] = useState(false);
  const [newInstitutionName, setNewInstitutionName] = useState('');
  const [newInstitutionEmail, setNewInstitutionEmail] = useState('');
  /*
    `other` is the resting answer (VALE-1577), so the step stays complete without
    the reader touching the selector — and it is also exactly what this picker
    mints today, which is what makes adding the choice a widening rather than a
    change of behaviour for anyone who ignores it.
  */
  const [newInstitutionType, setNewInstitutionType] = useState(
    DEFAULT_NEW_INSTITUTION_TYPE
  );

  const [editingCustom, setEditingCustom] = useState(false);
  /*
    **The near-duplicate step's state** (VALE-1422). `null` means the step is not
    showing; an array (possibly checked and found empty) means the pre-flight has run.
  */
  const [duplicateCandidates, setDuplicateCandidates] = useState<
    InstitutionDuplicateCandidate[] | null
  >(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editEmailValue, setEditEmailValue] = useState('');
  const [isSavingCustom, setIsSavingCustom] = useState(false);
  /** The remove-confirmation dialog's own open state (VALE-1627). */
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  /**
   * The query for which the "Other institution types" tier was expanded (VALE-1636).
   *
   * Stored as the query rather than a bare boolean so the tier re-collapses on its own
   * when the reader types on — `showOtherTypes` below derives from a match against the
   * live `searchValue`, with no reset effect to trip the `set-state-in-effect` lint rule.
   * Each new search therefore leads with its in-category results and keeps the
   * out-of-category rows one click away rather than in the reader's face.
   */
  const [otherTypesExpandedForQuery, setOtherTypesExpandedForQuery] = useState<
    string | null
  >(null);

  /*
    **The reader's sort choice, remembered** (VALE-1423).

    `best` is the default and stays the default: making alphabetical the default would
    penalise precision, which is the exact defect VALE-1420 just fixed. A–Z is the job
    Jessie at Beck Legal was actually doing — scanning a set she had already narrowed —
    so it is offered, not imposed. `Nearest` (proximity) is offered too but is a per-use
    choice, never the remembered default (VALE-1596).

    Read once from storage at mount. See {@link initialInstitutionSortMode} for why a
    lazy initialiser is safe here and a hydrating effect is not needed.

    Per browser rather than per user account, deliberately — this is a viewing
    preference about a dropdown, and a round trip to store it server-side would buy
    nothing a reader would notice.
  */
  // Opens on `best` (or a remembered `alpha`); a stale sticky `proximity` is normalised
  // back to `best` by the initialiser (VALE-1596), so a fresh picker never opens on
  // Nearest even though Nearest is offered to every firm (VALE-1957).
  const [sortMode, setSortMode] = useState<InstitutionSortMode>(() =>
    initialInstitutionSortMode()
  );
  // **Proximity browse (VALE-1588): "Nearest" selected with no query typed.** The default
  // listing is then the distance-ranked, radius-capped nearby set (not the curated
  // shortlist), so it must NOT be truncated to the 10-row shortlist cap below, and the
  // "Nearest" control must be reachable from the resting picker so it can be entered.
  const proximityBrowseActive =
    sortMode === 'proximity' && searchValue.length < 2;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const handleSortChange = (next: InstitutionSortMode) => {
    const crossesProximity =
      (sortMode === 'proximity') !== (next === 'proximity');
    setSortMode(next);
    try {
      window.localStorage.setItem(INSTITUTION_SORT_STORAGE_KEY, next);
    } catch {
      // Ignored for the same reason as the read above.
    }
    /*
      **Proximity is a backend re-rank, so entering or leaving it must re-fetch** (VALE-1573).
      `best` and `alpha` reorder the fetched page in place, but proximity changes which rows
      the backend returns and in what order, so toggling across it re-issues the search. The
      localStorage write above happens first, so the re-fetch (which reads that preference)
      sees the new mode. The 2-character gate mirrors the search box's own handler: below it,
      the picker shows the popular shortlist, which proximity does not apply to.
    */
    if (crossesProximity) {
      onInstitutionSearch?.(searchValue.length >= 2 ? searchValue : '');
    }
    /*
      **Caveat 1 of the two the spike flagged: focus.** Clicking the toggle takes DOM
      focus off `CommandInput`, after which typing goes nowhere — the popover looks
      alive and swallows every keystroke. `preventDefault` on the wrapper's mousedown
      stops the focus moving at all for a mouse; this puts it back for the keyboard and
      for any path that gets through.
    */
    searchInputRef.current?.focus();
  };

  /*
    **Ask for the default shortlist whenever the picker opens** (VALE-1031).

    The picker used to open on "Type at least 2 characters to search", so the most
    common case — reaching for one of the handful of institutions a firm deals with
    constantly — still cost typing, and the list gave no signal about what it held.

    **Keyed on `open`, deliberately, and NOT on the Popover's `onOpenChange`.**
    That callback only fires for Radix's own trigger, and this component opens
    itself directly via `setOpen(true)` from two other places: `handleOpenPicker`
    (the compact resting value, i.e. every EDIT of an item that already has an
    institution) and the custom-institution pencil. Hanging the request off
    `onOpenChange` therefore served the shortlist when ADDING an item and left the
    list empty when editing one — the same picker behaving two ways.

    Driving it from the component rather than from each parent is what makes every
    consumer benefit at once: this field is mounted by the inventory drawer, the
    add-inventory dialog, AI-document-import review, create-matter review and the
    Discovery assets column, and all five `onInstitutionSearch` handlers already
    read an empty query as "list the default page" (they only set `q` when the
    string is non-empty).
  */
  const requestedDefaultRef = useRef(false);
  useEffect(() => {
    if (!open) {
      // Re-arm, so the next open asks again rather than showing a stale list.
      requestedDefaultRef.current = false;
      return;
    }
    // Once per open: the search box's own handler owns every later request, and
    // re-firing here would fight it while the reader is typing.
    if (requestedDefaultRef.current || searchValue) return;
    requestedDefaultRef.current = true;
    onInstitutionSearch?.('');
  }, [open, searchValue, onInstitutionSearch]);

  // Derived, not stored (VALE-1636): the tier is open only while its expansion still
  // belongs to the current query, so typing on re-collapses it with no reset effect.
  const showOtherTypes = otherTypesExpandedForQuery === searchValue;

  /*
    **The default shortlist is capped at 10; a search is not** (VALE-1031).

    The parents fetch a search page of 50 and, for the default listing, ask the
    backend for the curated-popular ordering. Capping here rather than at each
    fetch keeps the cap in one place and true for every consumer, including any
    that hands over a longer list.
  */
  const shownInstitutions = useMemo(
    () =>
      // A search or a proximity browse (VALE-1588) shows the full returned set; only the
      // curated default shortlist is capped at 10. The proximity browse is already bounded
      // server-side to the radius, so the 10-row cap would wrongly hide nearby institutions.
      searchValue.length >= 2 || proximityBrowseActive
        ? institutions
        : institutions.slice(0, DEFAULT_SHORTLIST_SIZE),
    [institutions, searchValue, proximityBrowseActive]
  );

  /*
    **The tiers, and the order inside them** (VALE-1423). The partition is the
    backend's (`matchTier`); this only groups, orders within a group and caps the two
    close-match groups. A listing with no tier — the default shortlist, or a response
    from before this shipped — comes back as one ungrouped list, which is what the
    picker rendered before.
  */
  const tiers = useMemo(
    () => tierInstitutionResults(shownInstitutions, sortMode),
    [shownInstitutions, sortMode]
  );

  // Parse current value to get institution
  const currentInstitution = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object' && 'id' in value) {
      return value as Institution | CustomInstitution;
    }
    if (typeof value === 'string') {
      return (
        institutions.find(
          (inst) => inst.name.toLowerCase() === value.toLowerCase()
        ) || null
      );
    }
    return null;
  }, [value, institutions]);

  // Get string representation for display
  const institutionName = useMemo(() => {
    if (!value) return '';
    if (typeof value === 'object' && 'name' in value) return value.name;
    if (typeof value === 'string') return value;
    return '';
  }, [value]);

  // Read-only mode
  if (mode === 'read') {
    if (!institutionName) return null;
    return (
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-muted-foreground flex-shrink-0 text-sm">
          {institutionTypeName}:
        </span>
        <div className="flex min-w-0 items-center gap-2">
          {currentInstitution &&
            !isCustomInstitution(currentInstitution) &&
            currentInstitution.faviconUrl && (
              <img
                src={currentInstitution.faviconUrl}
                alt=""
                className="h-4 w-4 shrink-0 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          <span className="text-muted-foreground truncate text-sm font-medium">
            {institutionName}
          </span>
        </div>
      </div>
    );
  }

  // Edit mode handlers
  const handleSelect = (institution: Institution | null) => {
    onChange(institution || null);
    setOpen(false);
    setSearchValue('');
    setShowChangeInstitution(false);
  };

  /**
   * **One click opens the picker** (VALE-355) — hoisted out of the `Card`'s own
   * `onClick` so the compact resting value opens it the same way rather than
   * carrying a second copy of the gesture.
   */
  const handleOpenPicker = () => {
    if (disabled) return;
    setShowChangeInstitution(true);
    setOpen(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setShowChangeInstitution(false);
    setShowAddNew(false);
    setEditingCustom(false);
  };

  const commitNewInstitution = (acknowledgedIds?: number[]) => {
    const customInstitution: CustomInstitution = {
      id: -1,
      name: newInstitutionName.trim(),
      email: newInstitutionEmail.trim(),
      type: 'custom',
      isCustom: true,
      ...(acknowledgedIds?.length
        ? { acknowledgedDuplicateInstitutionIds: acknowledgedIds }
        : {}),
      /*
        Sent only where the step actually asked (VALE-1577). Absent everywhere
        else, so the backend keeps deriving the type from the inventory item type
        for every other picker — spread conditionally for the same reason as the
        acknowledgement above: the item DTOs use `forbidNonWhitelisted`, and an
        explicit `undefined` is noise on every create that never offered a choice.
      */
      ...(allowTypeSelection ? { institutionType: newInstitutionType } : {})
    };
    onChange(customInstitution);
    setShowAddNew(false);
    setDuplicateCandidates(null);
    setNewInstitutionName('');
    setNewInstitutionEmail('');
    setNewInstitutionType(DEFAULT_NEW_INSTITUTION_TYPE);
  };

  /*
    **Ask before handing the placeholder over** (VALE-1422).

    The institution is not created here — it is created by the inventory-item write
    that receives `institutionName`/`institutionEmail` — so the server's own
    near-duplicate 409 arrives after this popover has closed, which is too late to be
    a choice. Asking first is what lets the reader answer inside the surface they are
    working in.

    **A failed pre-flight proceeds rather than blocks.** The check is a courtesy, and
    the write still enforces it: if the reader has genuinely hit a duplicate they will
    get the 409 and its error, which is the pre-VALE-1422 behaviour rather than a new
    dead end. Refusing to let them continue because a GET failed would be worse than
    the duplicate this guards against.
  */
  const handleAddNew = async () => {
    if (!newInstitutionName.trim() || !newInstitutionEmail.trim()) return;

    setCheckingDuplicates(true);
    try {
      const result = await getInstitutionDuplicateCandidatesAction(
        newInstitutionName.trim()
      );
      if (result.success && result.data && result.data.length > 0) {
        setDuplicateCandidates(result.data);
        return;
      }
    } catch {
      // Fall through — see the note above.
    } finally {
      setCheckingDuplicates(false);
    }

    commitNewInstitution();
  };

  /** `Use this one` — the existing row is a real institution, so this is an ordinary pick. */
  const handleUseExistingDuplicate = (
    candidate: InstitutionDuplicateCandidate
  ) => {
    handleSelect({
      id: candidate.institutionId,
      name: candidate.name,
      type: candidate.type,
      email: candidate.email ?? undefined,
      isCustom: !candidate.isGlobal
    });
    setShowAddNew(false);
    setDuplicateCandidates(null);
    setNewInstitutionName('');
    setNewInstitutionEmail('');
    setNewInstitutionType(DEFAULT_NEW_INSTITUTION_TYPE);
  };

  const handleCancelAddNew = () => {
    // Back to the list, not out of the popover — the reader asked for a step, so
    // cancelling should return them to where they were rather than dismissing it.
    setShowAddNew(false);
    setDuplicateCandidates(null);
    setNewInstitutionName('');
    setNewInstitutionEmail('');
    setNewInstitutionType(DEFAULT_NEW_INSTITUTION_TYPE);
  };

  const handleEditCustom = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!currentInstitution || !isCustomInstitution(currentInstitution)) return;
    setNewInstitutionName(currentInstitution.name);
    setNewInstitutionEmail(currentInstitution.email);
    setShowAddNew(true);
    /*
      This is the *other* entry point to the create/edit step — the pencil on a
      custom institution's card, which is outside the popover. Now that the step
      lives inside the popover, opening it without opening the popover would leave
      the pencil doing nothing at all.
    */
    setOpen(true);
  };

  const handleStartEditCustom = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!currentInstitution || !isSavedCustomInstitution(currentInstitution))
      return;
    setEditNameValue(currentInstitution.name || '');
    setEditEmailValue(currentInstitution.email || '');
    setEditingCustom(true);
    /*
      **In compact the step lives inside the popover** (VALE-997), which is the
      other half of removing the resting row's pencil: the entry point is a
      `CommandItem` in the search list, so the popover is already open when this
      runs and only `editingCustom` selects which step it shows. In `form` the
      editor is still the one inside the card and this changes nothing.
    */
  };

  const handleSaveCustom = async () => {
    if (!currentInstitution || !isSavedCustomInstitution(currentInstitution))
      return;
    /*
      Both are required. The name is a firm's own record and must not be blanked
      to a placeholder, and the email is where estate correspondence is sent — so
      neither may be saved empty, matching the create step's own "both required"
      bar.
    */
    if (!editNameValue.trim() || !editEmailValue.trim()) return;

    setIsSavingCustom(true);
    const result = await updateInstitutionAction(currentInstitution.id, {
      name: editNameValue.trim(),
      email: editEmailValue.trim()
    });
    setIsSavingCustom(false);

    if (result.success) {
      onChange({
        ...currentInstitution,
        name: editNameValue.trim(),
        email: editEmailValue.trim()
      });
      setEditingCustom(false);
      // Back to the resting value, not to the search list — the reader came to
      // correct this institution's details, not to change institution.
      setOpen(false);
      setShowChangeInstitution(false);
      return;
    }

    /*
      **A failed save has to say so** (found in review of LF-6314).

      Without this branch the failure was completely silent: `isSavingCustom` reset,
      the inline editor stayed open with the typed values still in it, and nothing
      anywhere said the write had been rejected. The two readings available to the
      user were "it saved" (the spinner stopped) and "it is still saving" — and one
      of these fields is the address estate correspondence is sent to, so believing
      the first is how a letter goes to the wrong place.

      The editor deliberately stays open on failure: the values are still in hand and
      retrying is one click, where closing it would discard what they typed.
    */
    toast.error(
      result.error ?? 'Could not save the institution. Please try again.'
    );
  };

  const handleCancelEditCustom = () => {
    setEditingCustom(false);
    setEditNameValue('');
    setEditEmailValue('');
  };

  /**
   * Open the remove-confirmation dialog for a SAVED custom institution (VALE-1627).
   * Never offered for a pre-save `CustomInstitution` (id -1, discarded by Cancel/Clear
   * already) or a catalogue row — mirrors the edit affordance's own guard.
   *
   * **In `compact` the popover must close first.** Opening a `Dialog` while the
   * `Popover` is still open in the same commit risks the body-lock race the modal
   * layers share (`components/CLAUDE.md` "Sheet ↔ Dialog/AlertDialog in the same
   * commit") — so the confirm opens on the next frame, after the popover's own
   * close has committed.
   */
  const handleRemoveSaved = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!currentInstitution || !isSavedCustomInstitution(currentInstitution))
      return;
    setOpen(false);
    requestAnimationFrame(() => setRemoveConfirmOpen(true));
  };

  const handleConfirmRemove = async () => {
    if (!currentInstitution || !isSavedCustomInstitution(currentInstitution))
      return;

    const result = await deleteInstitutionAction(currentInstitution.id);
    if (!result.success) {
      // Thrown so `DeleteConfirmationDialog` catches it and shows the reason inline
      // (the "in use" 409 message) — the dialog stays open, matching the failed-save
      // pattern above rather than silently closing on a blocked removal.
      throw new Error(
        result.error ?? 'Could not remove the institution. Please try again.'
      );
    }

    toast.success(`${currentInstitution.name} removed.`);
    onChange(null);
    setShowChangeInstitution(false);
  };

  // Determine email to display for saved custom institutions
  const savedCustomEmail =
    currentInstitution && isSavedCustomInstitution(currentInstitution)
      ? currentInstitution.email
      : null;

  /**
   * **The provenance caption belongs to the ROW, not to the read-out**
   * (VALE-355).
   *
   * The caption under the institution's name — `Email: …`, and for a
   * not-yet-created one the `New {type} • Will be created…` line above it — used
   * to live only inside the `Card`, i.e. only in the at-rest state. Clicking the
   * row swapped the two-line card for the one-line combobox, so the row lost a
   * line the instant it was clicked and **every row beneath it jumped up**:
   * measured at **−25px** on a saved-custom provider, with the reader's pointer
   * still on the control. Reported as *"when I click it, it becomes one line"*.
   *
   * A row of this treatment must not change height when it opens — that is the
   * whole premise of "an editor in the value's own slot". So the caption is
   * derived once here and rendered by **both** states.
   *
   * **Showing the current institution's email under an open picker is accurate,
   * not stale.** The record still holds this institution until another is chosen;
   * the picker is a pending choice, not a cleared value. (`showAddNew` is the one
   * exception — the create step is describing an institution that does not exist
   * yet, so there is no provenance to caption.)
   */
  const provenanceCaption: string[] | null =
    !currentInstitution || showAddNew
      ? null
      : isCustomInstitution(currentInstitution)
        ? [
            `New ${institutionTypeName.toLowerCase()} • Will be created when matter is saved`,
            `Email: ${currentInstitution.email}`
          ]
        : isSavedCustomInstitution(currentInstitution)
          ? [`Email: ${savedCustomEmail || ATTRIBUTE_NOT_RECORDED}`]
          : null;

  const institutionTypeNameLower = institutionTypeName.toLowerCase();
  const institutionTypeNamePlural = institutionTypeNameLower.endsWith('y')
    ? institutionTypeNameLower.slice(0, -1) + 'ies'
    : institutionTypeNameLower + 's';

  /**
   * One group of the result list — its heading, its rows, and the count it collapsed.
   *
   * **Every row carries a second line saying which place it is** (VALE-1423). Twenty-one
   * of the catalogue's Respect facilities share one email address, and the list used to
   * show a favicon and a truncated name and nothing else. `describeInstitutionRow` owns
   * what that line says; this owns only how it is drawn.
   *
   * Returns `null` for an empty group rather than an empty `CommandGroup`, because a
   * heading above nothing is what `shouldFilter={false}` would otherwise leave behind.
   */
  const renderInstitutionGroup = (
    heading: React.ReactNode | undefined,
    rows: Institution[]
  ) => {
    if (rows.length === 0) return null;
    return (
      <CommandGroup heading={heading}>
        {rows.map((institution) => {
          const description = describeInstitutionRow(institution);
          return (
            <CommandItem
              key={institution.id}
              value={`${institution.name}-${institution.id}`}
              onSelect={() => handleSelect(institution)}
              className="items-start"
            >
              <Check
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  currentInstitution?.id === institution.id
                    ? 'opacity-100'
                    : 'opacity-0'
                )}
              />
              {institution.faviconUrl && (
                <img
                  src={institution.faviconUrl}
                  alt=""
                  className="mt-0.5 h-4 w-4 shrink-0 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{institution.name}</span>
                {description.length > 0 && (
                  <span className="text-muted-foreground truncate text-xs">
                    {description.join(' · ')}
                  </span>
                )}
              </div>
              {/*
                **Distance, only in proximity mode** (VALE-1596). Rendered off the value
                itself, not off the sort mode: the backend attaches `distanceKm` only when
                it distance-ranked (a proximity search or the no-query nearest browse) and
                sets it `null` on a coordinate-less row a proximity search still returns —
                so a plain number is exactly "this row has a distance worth showing". A
                non-proximity listing carries none and shows nothing.
              */}
              {typeof institution.distanceKm === 'number' && (
                <span className="text-muted-foreground mt-0.5 ml-auto shrink-0 text-xs tabular-nums">
                  {formatInstitutionDistanceKm(institution.distanceKm)}
                </span>
              )}
            </CommandItem>
          );
        })}
      </CommandGroup>
    );
  };

  const body = (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1',
        // The value slot, so selected and cleared occupy the same box as the money
        // and select cells beside them — see the `Card` note below.
        compact ? 'flex-1' : 'min-w-[200px]'
      )}
    >
      {/*
        In compact the label is the row's own column (see the `field` wrapper
        below), so the field never renders a second one of its own — a stacked
        `text-sm` label above an attribute row is the mixed-idiom seam this variant
        exists to remove.
      */}
      {!hideLabel && !compact && (
        <label className="text-muted-foreground text-sm font-medium">
          {institutionTypeName}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      {disabled && (
        <p className="text-muted-foreground text-xs">
          The {institutionTypeName.toLowerCase()} can no longer be changed once
          the item has been collected, resolved, or paid out.
        </p>
      )}

      {/* Show institution card if selected and not changing */}
      {currentInstitution && !showChangeInstitution && !showAddNew ? (
        compact ? (
          /*
          **At rest the compact picker is a VALUE, not a control** (VALE-997).

          It wore {@link ATTRIBUTE_EDITOR_CHROME} — the ring and fill an *open*
          editor wears — so the one row on the panel that was not being edited
          was the only one drawing a box, and the reporter read it as a card
          dropped into the form. Worse, the box was tinted `border-primary
          bg-primary/5` for a firm-created institution and plain for a catalogue
          one, so the identical field looked different depending on which
          institution happened to be in it, with nothing about the asset type
          differing.

          This is the treatment `identity-variant-attribute-rows.tsx` already
          established for a `render` row: bare, on the row's own inset and type,
          with the hover tint as the only chrome and the clear affordance
          revealed on hover like every other row's. A catalogue institution, a
          saved custom one and a not-yet-created one are now identical here
          apart from {@link provenanceCaption} — the `+` mark and the inline
          pencil are gone, the latter because editing a custom institution
          belongs in the search popover (see the `Edit` items in the list).

          `group/cell` is what {@link ATTRIBUTE_AFFORDANCE_REVEALED} reveals
          against — a normal row inherits it from `ATTRIBUTE_CELL`, a `render`
          row has to establish its own.
        */
          <div
            className={cn(
              // The open state's own gap between the control and the caption, so
              // the row does not change height as it opens (VALE-355). Always a
              // real box, never `display: contents` — `group/cell` has to have
              // one to hover-test against.
              'group/cell flex min-w-0 flex-col gap-2'
            )}
          >
            <div className="flex min-w-0 items-center gap-1">
              <button
                type="button"
                disabled={disabled}
                onClick={handleOpenPicker}
                className={cn(
                  ATTRIBUTE_VALUE_TEXT,
                  ATTRIBUTE_VALUE_INK,
                  ATTRIBUTE_INSET,
                  ATTRIBUTE_LEADING,
                  ATTRIBUTE_EDITOR_BARE,
                  ATTRIBUTE_RADIUS,
                  /*
                    **`flex-1`, so the row is the same width as every other
                    field** (VALE-1181). The button hugged its own text, so a
                    picker read narrower than its neighbours on a short
                    institution name and ran out to the column edge on a long
                    one — the only rows on an attribute grid whose width tracked
                    their content, while every value target beside them fills
                    the cell (`ATTRIBUTE_VALUE_TARGET`, VALE-349).

                    Filling is also what makes the `truncate` below do anything:
                    a hugging box has no boundary to ellipsise against, so a long
                    name simply grew the control instead.
                  */
                  'flex min-w-0 flex-1 items-center gap-2 text-left transition-colors',
                  disabled ? 'cursor-default' : ATTRIBUTE_HOVER
                )}
              >
                {!isCustomInstitution(currentInstitution) &&
                  !currentInstitution.isCustom &&
                  currentInstitution.faviconUrl && (
                    /*
                    The logo as a small inline mark, on the value's own line —
                    `h-4 w-4` rather than the card's `h-5 w-5`, which was taller
                    than the 20px line it sat on and set the row's height by
                    itself.
                  */
                    <img
                      src={currentInstitution.faviconUrl}
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                <span className="truncate">{currentInstitution.name}</span>
              </button>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  title="Clear selection"
                  aria-label={`Clear ${institutionTypeNameLower}`}
                  className={cn(
                    ATTRIBUTE_AFFORDANCE,
                    ATTRIBUTE_AFFORDANCE_REVEALED
                  )}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {/*
            Rendered from {@link provenanceCaption} rather than from a second
            hand-written copy, and with the open state's exact wrapper — that
            derivation is what keeps the two states the same height, and it used
            to be honoured only on the open side.
          */}
            {provenanceCaption && (
              <div className="flex flex-col gap-1 px-1.5 text-xs">
                {provenanceCaption.map((line) => (
                  <p key={line} className={ATTRIBUTE_MUTED_INK}>
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Card
            className={cn(
              /*
              **The `form` variant's card, and only its** (VALE-997). The compact
              arm above no longer comes through here at all: dressing a `Card` up
              as a value cell — ring, fill, inset, `border-0 shadow-none` — was
              the long way round to "this row should not be a box", and it was
              still a box. The tokens it borrowed for that are gone with it.
            */
              'relative px-3 py-1.5 transition-all',
              !disabled && 'hover:bg-muted/50 cursor-pointer',
              isCustomInstitution(currentInstitution) &&
                'border-primary bg-primary/5',
              isSavedCustomInstitution(currentInstitution) &&
                'border-primary/50 bg-primary/5',
              error && 'border-destructive'
            )}
            /*
            **One click opens the picker** (VALE-355).

            This used to set `showChangeInstitution` alone, which swapped the card
            for the combobox trigger and left the popover *shut* — so the first
            click on the first field of the record changed how the field looked and
            achieved nothing, and the reader had to click the same spot again to get
            a list. Reported as *"the first click changes the state of the field but
            it does nothing"*, and it is the substance behind the ticket's
            "row collapses when clicked": what a reader notices is the row changing
            shape, but what is actually wrong is that the gesture was spent without
            opening anything.

            Every other row of this treatment opens its editor *ready to use* on one
            click — the ownership `select` passes `defaultOpen`, the address and name
            rows open their popover — so a two-click field was also the one row here
            that did not behave like its neighbours.
          */
            onClick={handleOpenPicker}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {!isCustomInstitution(currentInstitution) &&
                    !currentInstitution.isCustom &&
                    currentInstitution.faviconUrl && (
                      <img
                        src={currentInstitution.faviconUrl}
                        alt=""
                        className="h-5 w-5 shrink-0 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  {(isCustomInstitution(currentInstitution) ||
                    isSavedCustomInstitution(currentInstitution)) && (
                    <Plus className="text-primary h-4 w-4" />
                  )}
                  <span className="font-medium">{currentInstitution.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  {isCustomInstitution(currentInstitution) && !disabled && (
                    <Button
                      pad="block"
                      type="button"
                      variant="ghost"
                      size="auto"
                      onClick={handleEditCustom}
                      title="Edit"
                    >
                      <Edit2 className="text-muted-foreground h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isSavedCustomInstitution(currentInstitution) &&
                    !disabled && (
                      <Button
                        pad="block"
                        type="button"
                        variant="ghost"
                        size="auto"
                        onClick={handleStartEditCustom}
                        title="Edit institution"
                      >
                        <Edit2 className="text-muted-foreground h-3.5 w-3.5" />
                      </Button>
                    )}
                  {isSavedCustomInstitution(currentInstitution) &&
                    !disabled && (
                      <Button
                        pad="block"
                        type="button"
                        variant="ghost"
                        size="auto"
                        onClick={handleRemoveSaved}
                        title="Remove institution"
                      >
                        <Trash2 className="text-muted-foreground h-3.5 w-3.5" />
                      </Button>
                    )}
                  {onViewInstitution &&
                    !isCustomInstitution(currentInstitution) &&
                    !currentInstitution.isCustom && (
                      <Button
                        pad="block"
                        type="button"
                        variant="ghost"
                        size="auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewInstitution(currentInstitution);
                        }}
                      >
                        <ExternalLink className="text-muted-foreground h-3.5 w-3.5" />
                      </Button>
                    )}
                  {!disabled && (
                    <Button
                      pad="block"
                      type="button"
                      variant="ghost"
                      size="auto"
                      onClick={handleClear}
                      title="Clear selection"
                    >
                      <X className="text-muted-foreground h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              {isCustomInstitution(currentInstitution) && (
                <div className="flex flex-col gap-1 text-xs">
                  <p className="text-muted-foreground">
                    New {institutionTypeName.toLowerCase()} • Will be created
                    when matter is saved
                  </p>
                  <p className="text-muted-foreground">
                    Email: {currentInstitution.email}
                  </p>
                </div>
              )}
              {isSavedCustomInstitution(currentInstitution) && (
                <div className="flex flex-col gap-1 text-xs">
                  {editingCustom ? (
                    <div className="flex flex-col gap-2">
                      <Input
                        type="text"
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        className="h-7 text-xs"
                        placeholder="Institution name"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Input
                        type="email"
                        value={editEmailValue}
                        onChange={(e) => setEditEmailValue(e.target.value)}
                        className="h-7 text-xs"
                        placeholder="Institution email"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="xs"
                          density="compact"
                          gap="standard"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveCustom();
                          }}
                          disabled={
                            !editNameValue.trim() ||
                            !editEmailValue.trim() ||
                            isSavingCustom
                          }
                        >
                          {isSavingCustom ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </Button>
                        <Button
                          size="xs"
                          density="compact"
                          type="button"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelEditCustom();
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      Email: {savedCustomEmail || '—'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        )
      ) : (
        <div
          className={cn(
            'flex flex-col',
            // Matches the Card's own inner `gap-2` so the open state's two lines
            // sit exactly where the at-rest state's two lines sat (VALE-355).
            compact && provenanceCaption ? 'gap-2' : 'contents'
          )}
        >
          {/* Searchable dropdown */}
          <Popover
            open={open}
            /*
              **Dismissing the popover must also leave the create step**, or
              `showAddNew` survives with the popover shut — and that is not a
              harmless leftover flag. It is read by two other branches: the
              institution Card is gated on `!showAddNew`, and so is the trigger's
              own label. So a user who opened the step from the pencil on a custom
              institution and then clicked outside would watch their selected
              institution disappear, with the trigger falling back to
              "Select institution…". Found in review, not by rendering it, because
              it needs the pencil entry point *plus* an outside click.
            */
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);
              if (!nextOpen) {
                setShowAddNew(false);
                setNewInstitutionName('');
                setNewInstitutionEmail('');
                setNewInstitutionType(DEFAULT_NEW_INSTITUTION_TYPE);
                /*
                  The edit step is a popover step in compact (VALE-997), so it
                  has to be left on dismiss for the same reason `showAddNew` is:
                  a flag surviving a shut popover is a state nothing on screen
                  can get out of — and in `form` it would re-open the card's own
                  inline editor unbidden.
                */
                setEditingCustom(false);
                /*
                  **Dismissing the picker returns to the card** (VALE-355).

                  `showChangeInstitution` used to survive a dismiss, so a reader who
                  opened the list and changed their mind was left on a bare combobox
                  trigger — and the trigger carries none of the card's affordances:
                  no `×` to clear the institution, no `Email:` caption, no pencil. The
                  clear button simply disappeared for the rest of the drawer's life,
                  with nothing to bring it back.

                  It was reachable before (the old two-click path ended at the same
                  trigger) but became easy to hit the moment one click opened the list.
                  Choosing an institution already came back here via `handleSelect`;
                  cancelling now does too, which is also the honest reading of a
                  dismiss — *"never mind, leave it as it was"*.
                */
                setShowChangeInstitution(false);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                disabled={loadingInstitutions || disabled}
                className={cn(
                  'bg-input-bg h-auto w-full justify-between rounded-md border-0 px-3 py-1.5 text-sm font-normal',
                  /*
                    The row's own inset and type, so an unset institution reads as
                    an empty attribute row rather than as a control parked in the
                    middle of one. `bg-transparent` from the bare token clears the
                    trigger's own grey fill.
                  */
                  compact &&
                    `${ATTRIBUTE_EDITOR_BARE} ${ATTRIBUTE_INSET} ${ATTRIBUTE_VALUE_TEXT} ${ATTRIBUTE_LEADING}`,
                  /*
                    **The ring and fill belong to the OPEN state only** (VALE-997).

                    They were unconditional in compact, so an unset picker sat in a
                    box while `Set BSB…` two rows down sat in nothing — the same
                    "reads as a control, not a value" defect as the selected state
                    above, on the other half of the branch. Open, the chrome is
                    correct and stays: it is then genuinely an editor.
                  */
                  compact && (open ? ATTRIBUTE_EDITOR_CHROME : ATTRIBUTE_HOVER),
                  error && 'border-destructive border'
                )}
              >
                {currentInstitution && !showAddNew ? (
                  <div className="flex items-center gap-2">
                    {!isCustomInstitution(currentInstitution) &&
                      currentInstitution.faviconUrl && (
                        <img
                          src={currentInstitution.faviconUrl}
                          alt=""
                          className="h-4 w-4 shrink-0 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    <span className={cn(compact && ATTRIBUTE_VALUE_INK)}>
                      {currentInstitution.name}
                    </span>
                  </div>
                ) : (
                  <span
                    className={cn(
                      compact ? ATTRIBUTE_MUTED_INK : 'text-muted-foreground'
                    )}
                  >
                    {compact
                      ? `Set ${institutionTypeName.toLowerCase()}…`
                      : `Select ${institutionTypeName.toLowerCase()}...`}
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            {/*
              **A named popover width, not the trigger's**
              ({@link ATTRIBUTE_POPOVER_W_WIDE}, VALE-997 then VALE-1423).

              LF-6314 bound this to `--radix-popover-trigger-width` because a
              popover narrower than its field reads as unanchored from it, and
              at the time the trigger was a full-width control so the two were
              the same thing. VALE-997 made the resting row a value that hugs its
              own text, and the variable quietly changed meaning with it: it
              became the width of whichever institution is selected.
              `Computershare` yielded a 148px popover in which the search box,
              the empty-state hint and "Add a new share registry" every one of
              them wrapped.

              The anchoring LF-6314 wanted comes from `align="start"` — the
              popover's left edge on the value's left edge — which is the part
              that was actually load-bearing.

              VALE-1423 then moved this one picker off the shared
              {@link ATTRIBUTE_POPOVER_W} (272px) onto its own 360px token. Its rows
              now carry a second line saying which of twenty-one near-identically
              named facilities each one is, and 272px truncated the name alone. The
              other seven attribute popovers keep the shared width — widening that to
              serve one caller is how a token stops meaning what its name says.
            */}
            <PopoverContent
              className={cn(
                'p-0',
                /*
                  **In `form` the panel matches the trigger; in `compact` it does not**
                  (VALE-1497).

                  LF-6314 bound this to `--radix-popover-trigger-width` because a
                  popover narrower than its field reads as unanchored from it. VALE-997
                  removed that binding, correctly — but for a reason that belongs to
                  ONE variant: `compact`'s resting row is a value that hugs its own
                  text, so the variable had quietly become "the width of whichever
                  institution is selected", and `Computershare` yielded a 148px popover
                  in which every line wrapped.

                  `form`'s trigger is `w-full` and has no such problem, and dropping
                  the binding for it too cost something visible the moment this picker
                  was mounted in a wide host: in the redemption sheet's broker step the
                  trigger is ~660px and the panel 360px, which strands half a sentence
                  of the callout behind it and truncates a row's email mid-address.

                  So: the fixed token is now a FLOOR rather than the width. A narrow
                  `form` host keeps exactly the 360px VALE-1423 sized for the two-line
                  rows; a wide one fills its trigger. `compact` is untouched.
                */
                compact
                  ? ATTRIBUTE_POPOVER_W_WIDE
                  : 'w-[var(--radix-popover-trigger-width)] min-w-[22.5rem]'
              )}
              align="start"
            >
              {showAddNew && duplicateCandidates ? (
                /*
                  **A third step, ahead of the create form** (VALE-1422). Shown only
                  after the pre-flight actually found something, so an ordinary inline
                  create is unchanged — it never sees this.
                */
                <NearDuplicateStep
                  /*
                    "Already exists", not "Already added": a Vale catalogue candidate
                    was never added by this firm, so "added" is the wrong assertion for
                    the state that matters most (taking the catalogue row is the
                    outcome this panel is steering towards).
                  */
                  title="Already exists"
                  typedName={newInstitutionName.trim()}
                  candidates={duplicateCandidates}
                  busy={checkingDuplicates}
                  onUseExisting={handleUseExistingDuplicate}
                  onAddAnyway={() =>
                    commitNewInstitution(
                      duplicateCandidates.map((c) => c.institutionId)
                    )
                  }
                  /*
                    Back to the create form with the typed values intact — the reader
                    may want to correct the name rather than take either option, and
                    losing what they typed would be its own defect.
                  */
                  onBack={() => setDuplicateCandidates(null)}
                />
              ) : showAddNew ? (
                <InlineCreateStep
                  title={`New ${institutionTypeNameLower}`}
                  fields={[
                    {
                      // Just `Name` / `Email`: the step's title already says
                      // which entity is being created, so repeating the type in
                      // each label read as `Stock Broker name`.
                      label: 'Name',
                      value: newInstitutionName,
                      placeholder: 'e.g. Commonwealth Bank',
                      autoFocus: true,
                      onChange: setNewInstitutionName
                    },
                    {
                      label: 'Email',
                      value: newInstitutionEmail,
                      placeholder: 'e.g. estates@commbank.com.au',
                      type: 'email',
                      onChange: setNewInstitutionEmail
                    }
                  ]}
                  // Short enough to read at a glance: three lines of explanation
                  // on a popover this size competed with the fields above it.
                  hint="Both required. The email receives estate correspondence."
                  submitLabel={`Add ${institutionTypeNameLower}`}
                  onCancel={handleCancelAddNew}
                  onSubmit={handleAddNew}
                  /*
                    The type choice, on the `Other` picker only (VALE-1577). A
                    render prop rather than a value, because fetching the options
                    needs a hook and this component is mounted by ~20 pickers that
                    do not want one — see {@link InstitutionTypeSelect}.
                  */
                  renderSelects={
                    allowTypeSelection
                      ? () => (
                          <InstitutionTypeSelect
                            value={newInstitutionType}
                            onChange={setNewInstitutionType}
                            side={allowTypeSelection}
                          />
                        )
                      : undefined
                  }
                />
              ) : compact && editingCustom && currentInstitution ? (
                /*
                  **Editing a saved custom institution, in the popover**
                  (VALE-997). The resting row's pencil is gone — it was one of
                  the three things that made the same field look different
                  depending on which institution was in it — so this step is now
                  reached from the `Edit …` item in the list below, which is
                  where the ticket puts it: *"editing a custom institution
                  belongs in the search popover, not in the resting row."*

                  Name and email are editable — both are what
                  {@link updateInstitutionAction} accepts and both are required
                  (VALE-1600): a firm's own institution can be created with an
                  incomplete name and must be correctable from here. `form` keeps
                  its own inline editor inside the card.
                */
                <InlineCreateStep
                  title={`Edit ${currentInstitution.name}`}
                  fields={[
                    {
                      label: 'Name',
                      value: editNameValue,
                      placeholder: 'e.g. Commonwealth Bank',
                      autoFocus: true,
                      onChange: setEditNameValue
                    },
                    {
                      label: 'Email',
                      value: editEmailValue,
                      placeholder: 'e.g. estates@commbank.com.au',
                      type: 'email',
                      onChange: setEditEmailValue
                    }
                  ]}
                  hint="Both required. The email receives estate correspondence."
                  submitLabel={isSavingCustom ? 'Saving…' : 'Save'}
                  onCancel={handleCancelEditCustom}
                  onSubmit={() => void handleSaveCustom()}
                />
              ) : (
                <Command shouldFilter={false}>
                  <CommandInput
                    ref={searchInputRef}
                    placeholder={`Search ${institutionTypeName.toLowerCase()}s...`}
                    value={searchValue}
                    onValueChange={(val) => {
                      setSearchValue(val);
                      /*
                        Searching itself is unchanged — the same 2-character
                        minimum, the same matching and ordering. What changed is
                        what happens BELOW it: a single character, or clearing the
                        input, now asks for the default shortlist again instead of
                        leaving the last search's results (or nothing) on screen.
                      */
                      onInstitutionSearch?.(val.length >= 2 ? val : '');
                    }}
                  />
                  {/*
                    **The bar between the search box and the list** (VALE-1423), which
                    is where it has to be: outside `CommandList`, so the toggle is
                    never a `CommandItem` and cannot be reached by arrow-key list
                    navigation.

                    It says two things the picker never said. **How much list there
                    is** — `229` is a number a person reacts to, and nothing used to
                    tell the reader what was behind the scroll. And **which order they
                    are in**, with the choice to change it.

                    The sort toggle is present whenever there is a tiered result to
                    sort; only the label to its left swaps. With a containment tier the
                    label is the count; with none it is the sentence that replaces
                    "No facilities found." — the catalogue does hold near misses, and
                    saying nothing found taught readers to create a duplicate row. The
                    toggle used to live inside the count arm and so vanished whenever a
                    search returned only close matches, stranding a full, sortable list
                    with no control to reorder it (VALE-1517).
                  */}
                  {/*
                    **The sort bar shows on the resting shortlist too, not only on a search**
                    (VALE-1588). "Nearest" has to be reachable before the reader types anything,
                    the whole point of the browse mode is defaulting the list to nearby
                    institutions, so the bar renders whenever there is a list and the label to its
                    left carries the browse count (or a "nothing nearby" line) rather than a search
                    count. Proximity is offered to every firm (VALE-1957).
                  */}
                  {!loadingInstitutions && (
                    <div className="bg-muted/40 flex items-center justify-between gap-2 border-b px-3 py-1.5">
                      {tiers.tiered ? (
                        tiers.contains.length > 0 ? (
                          <span className="text-muted-foreground min-w-0 text-xs">
                            {shownInstitutions.length}{' '}
                            {shownInstitutions.length === 1
                              ? institutionTypeNameLower
                              : institutionTypeNamePlural}
                          </span>
                        ) : (
                          <span className="text-muted-foreground min-w-0 text-xs">
                            No {institutionTypeNameLower} contains &ldquo;
                            {searchValue.trim()}&rdquo;. Closest by spelling:
                          </span>
                        )
                      ) : (
                        <span className="text-muted-foreground min-w-0 text-xs">
                          {proximityBrowseActive &&
                          shownInstitutions.length === 0
                            ? `No ${institutionTypeNameLower} found nearby`
                            : `${shownInstitutions.length} ${
                                shownInstitutions.length === 1
                                  ? institutionTypeNameLower
                                  : institutionTypeNamePlural
                              }`}
                        </span>
                      )}
                      {/*
                        **The two caveats the spike flagged, both handled here.**
                        Nothing else in this repo puts an interactive control inside
                        a `Command`, so neither was assumed.

                        *Focus*: `preventDefault` on mousedown stops DOM focus
                        leaving `CommandInput`, after which typing would go nowhere.

                        *Enter*: cmdk binds Enter on the `Command` root and Radix
                        Toggle also activates on Enter, so one keypress with the
                        toggle focused would otherwise both switch the sort AND
                        select whichever row was highlighted. Space is stopped for
                        the same reason.
                      */}
                      <div
                        className="shrink-0"
                        onMouseDown={(event) => event.preventDefault()}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                          }
                        }}
                      >
                        {/*
                            **The three-way sort control** (VALE-1573; generally available,
                            VALE-1957). A `ToggleGroup` rather than the two-segment
                            `AttributeSegmentedBoolean`, whose sliding-pill geometry is
                            50%-per-segment and cannot carry a third option. `type="single"` with
                            no `value` falling back is guarded below: an empty toggle (the reader
                            clicking the active segment off) is ignored, so a mode is always
                            selected.
                          */}
                        <ToggleGroup
                          type="single"
                          size="sm"
                          variant="outline"
                          value={sortMode}
                          onValueChange={(next) => {
                            if (next)
                              handleSortChange(next as InstitutionSortMode);
                          }}
                          aria-label={`Sort ${institutionTypeNamePlural}`}
                        >
                          {/* Smaller than the sm default (h-9/text-sm): the sort
                                control is secondary chrome above the list, so it takes
                                less height and a smaller label (VALE-1596). */}
                          <ToggleGroupItem
                            value="best"
                            className="h-7 min-w-0 px-2 text-xs"
                          >
                            Best match
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="alpha"
                            className="h-7 min-w-0 px-2 text-xs"
                          >
                            A–Z
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="proximity"
                            className="h-7 min-w-0 px-2 text-xs"
                          >
                            Nearest
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                    </div>
                  )}
                  {/*
                    **Taller than the shared 410px, so the create action is not pushed
                    out of view by the group headings** (VALE-1423, found at sign-off).

                    The tiers cost real vertical space that a flat list did not: up to
                    three headings and two-line rows instead of one. Measured on
                    `respect st` (three containment rows and a short catalogue group)
                    that is ~473px of content, so at 410px `Add a new …` sat below the
                    fold on a result set of only eight rows. Which is the worst possible
                    moment to hide it: a reader who cannot see the row they want and
                    cannot see the create action is the reader who goes and makes a
                    duplicate somewhere else.

                    Every close match the search returned is now rendered in full, so a
                    large set is a list you scroll, never one truncated behind a count
                    (VALE-1423, corrected at sign-off). A long list still scrolls, and
                    should. This height is about keeping the create action reachable on
                    the short results the tiering made tall.

                    **`min(…,60dvh)`, not a bare 480px** (VALE-1613). `cn()` merges
                    Tailwind classes by group, so this override replaced the base
                    `CommandList`'s whole `max-h-[min(410px,60dvh)]` — including the
                    `60dvh` clamp that keeps it inside a short window. A flat 480px cap
                    ran off the bottom of the window on a laptop-height viewport with
                    only a handful of results.
                  */}
                  <CommandList className="max-h-[min(480px,60dvh)]">
                    {loadingInstitutions ? (
                      <div className="text-muted-foreground p-4 text-center text-sm">
                        Searching...
                      </div>
                    ) : institutions.length === 0 && searchValue.length >= 2 ? (
                      <CommandEmpty>
                        No {institutionTypeNamePlural} found.
                      </CommandEmpty>
                    ) : institutions.length === 0 && proximityBrowseActive ? (
                      // Proximity browse with nothing inside the radius (VALE-1588): say so,
                      // and point at the way out — the other sorts are not radius-capped, and
                      // a typed search surfaces a distant exact match (the probate guard).
                      <CommandEmpty>
                        No {institutionTypeNamePlural} found nearby. Switch to
                        Best match or A–Z to see all, or search by name.
                      </CommandEmpty>
                    ) : (
                      <>
                        {/*
                          **Three groups, and the sort never crosses them**
                          (VALE-1423). A group is rendered only when it holds
                          something: the picker sets `shouldFilter={false}`, so cmdk
                          does no filtering and will not auto-hide a heading that
                          ends up above nothing.

                          The untiered case — the default shortlist, or a response
                          from before this shipped — falls through the same first
                          group with no heading, which is exactly the single plain
                          list this picker rendered before.
                        */}
                        {renderInstitutionGroup(
                          tiers.tiered ? (
                            <span className="flex items-baseline justify-between gap-2">
                              <span className="truncate">
                                Contains &ldquo;{searchValue.trim()}&rdquo;
                              </span>
                              <span className="shrink-0 tabular-nums">
                                {tiers.contains.length}
                              </span>
                            </span>
                          ) : undefined,
                          tiers.contains
                        )}
                        {renderInstitutionGroup(
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate">
                              Close matches · your firm
                            </span>
                            <span className="shrink-0 tabular-nums">
                              {tiers.firmCloseMatches.length}
                            </span>
                          </span>,
                          tiers.firmCloseMatches
                        )}
                        {renderInstitutionGroup(
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate">
                              Close matches · Vale catalogue
                            </span>
                            <span className="shrink-0 tabular-nums">
                              {tiers.catalogueCloseMatches.length}
                            </span>
                          </span>,
                          tiers.catalogueCloseMatches
                        )}
                        {/*
                          **The out-of-category tier, collapsed by default** (VALE-1636).
                          Fuzzy rows whose type is not the one this asset implies (a bank
                          account + "respect" pulling in aged-care providers) are demoted
                          here rather than mixed among the in-category results. Nothing is
                          hidden — the toggle expands them and each is selectable, so a
                          curated global stays reachable (VALE-1041). Empty outside an
                          asset-type context, so a type-less search never renders this.
                        */}
                        {tiers.otherTypeMatches.length > 0 && (
                          <>
                            {/*
                              The empty-in-category state is already spoken by the sort bar
                              above ("No {type} contains …"), so this tier adds only the
                              collapsed group, not a second "nothing matched" line.
                            */}
                            <CommandSeparator />
                            <CommandGroup>
                              <CommandItem
                                value="__toggle_other_institution_types"
                                onSelect={() =>
                                  setOtherTypesExpandedForQuery(
                                    showOtherTypes ? null : searchValue
                                  )
                                }
                                className="text-muted-foreground"
                              >
                                <ChevronDown
                                  className={cn(
                                    'h-4 w-4 shrink-0 transition-transform',
                                    showOtherTypes ? '' : '-rotate-90'
                                  )}
                                />
                                <span className="flex-1 truncate">
                                  Other institution types
                                </span>
                                <span className="shrink-0 tabular-nums">
                                  {tiers.otherTypeMatches.length}
                                </span>
                              </CommandItem>
                            </CommandGroup>
                            {showOtherTypes &&
                              renderInstitutionGroup(
                                undefined,
                                tiers.otherTypeMatches
                              )}
                          </>
                        )}
                      </>
                    )}
                    {/*
                    **"Add new" is the last item in the list, not a helper row
                    under the field** (LF-6314).

                    It used to sit outside the popover as `Can't find the {type}?
                    + Add new {type}` — two elements on one line beneath the
                    control. That had three problems, in increasing order of
                    importance:

                    · It wrapped. At the inventory drawer's width the question
                      broke onto a second line while the action stayed on the
                      first, leaving the link orphaned from the sentence it
                      belonged to and crowding the next field's label. Measured
                      at 148px available in the drawer, against the ~230px the
                      one-line version needs.
                    · It asked the question in the wrong place. You only discover
                      the institution is missing *after* searching for it, and by
                      then you are looking inside the popover — where there was
                      nothing to click.
                    · It cost every consumer vertical space permanently to
                      advertise an action almost nobody takes on any given edit.

                    Inside the list it is where the search happens, it is
                    unreachable until the popover is open (so it cannot compete
                    with the field), and it cannot wrap into a neighbour. This is
                    the same shape the codebase's other create-inline pickers
                    use — a trailing `CommandItem` after a separator.

                    It renders regardless of `searchValue` length so the default
                    shortlist and the "no results" state both still offer it —
                    those are exactly the moments a user concludes the institution
                    is not there. (The "type at least 2 characters" state this
                    originally named is gone: the picker now opens on a shortlist,
                    VALE-1031.)
                  */}
                    {/*
                      **The entry point the resting row's pencil used to be**
                      (VALE-997). Offered only for an institution the firm owns —
                      there is nothing to edit on a catalogue one — and only in
                      compact, since `form` still carries the pencil on its card.

                      A pre-save custom institution routes to the two-field
                      create step prefilled ({@link handleEditCustom}); a saved
                      one routes to the name-and-email edit step above.
                    */}
                    {compact &&
                      !disabled &&
                      currentInstitution &&
                      (isCustomInstitution(currentInstitution) ||
                        isSavedCustomInstitution(currentInstitution)) && (
                        <>
                          <CommandSeparator />
                          <CommandGroup>
                            <CommandItem
                              value={`__edit_${institutionTypeName}`}
                              onSelect={() =>
                                isCustomInstitution(currentInstitution)
                                  ? handleEditCustom()
                                  : handleStartEditCustom()
                              }
                              className="text-primary"
                            >
                              <Edit2 className="mr-2 h-4 w-4 shrink-0" />
                              Edit {currentInstitution.name}
                            </CommandItem>
                          </CommandGroup>
                        </>
                      )}
                    {/*
                      **Remove is offered only once the institution is actually SAVED**
                      (VALE-1627) — a pre-save `CustomInstitution` (id -1) has nothing to
                      remove yet; Clear/Cancel already discards it. `handleRemoveSaved`
                      closes this popover before opening the confirm dialog, on the next
                      frame, so the two modal layers never overlap in one commit.
                    */}
                    {compact &&
                      !disabled &&
                      currentInstitution &&
                      isSavedCustomInstitution(currentInstitution) && (
                        <>
                          <CommandSeparator />
                          <CommandGroup>
                            <CommandItem
                              value={`__remove_${institutionTypeName}`}
                              onSelect={() => handleRemoveSaved()}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4 shrink-0" />
                              Remove {currentInstitution.name}
                            </CommandItem>
                          </CommandGroup>
                        </>
                      )}
                    {!disabled && (
                      <>
                        <CommandSeparator />
                        <CommandGroup>
                          <CommandItem
                            value={`__add_new_${institutionTypeName}`}
                            /*
                            **Keeps the popover open and advances it to the create
                            step** (LF-6314 round 2). It used to close the popover
                            and reveal a panel *below* the field, which threw the
                            reader out of the surface they were working in and made
                            a two-part action feel like two unrelated ones. Staying
                            put reads as a next step.
                          */
                            onSelect={() => setShowAddNew(true)}
                            className="text-primary"
                          >
                            <Plus className="mr-2 h-4 w-4 shrink-0" />
                            Add a new {institutionTypeName.toLowerCase()}
                          </CommandItem>
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              )}
            </PopoverContent>
          </Popover>
          {/*
            The at-rest card's own caption, kept while the picker is open so the
            row does not change height — see {@link provenanceCaption}. Static
            here rather than the card's editable variant: the pencil that starts
            an email edit lives on the card, and a second edit affordance inside
            an open picker would be two pending changes in one row.

            `gap-2` and the horizontal inset are the Card's, not this wrapper's
            `gap-1` — the two states have to agree to the pixel, which is the
            defect this is fixing.
          */}
          {compact && provenanceCaption && (
            <div className="flex flex-col gap-1 px-1.5 text-xs">
              {provenanceCaption.map((line) => (
                <p key={line} className={ATTRIBUTE_MUTED_INK}>
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/*
        The field's validation error. It sits here, outside the popover, because it
        belongs to the *field* rather than to either step — and it survived the
        removal of the old create panel only because the zero-warning lint gate
        flagged `AlertCircle` as newly unused.
      */}
      {error && (
        <p className="text-destructive mt-1 flex items-center gap-1 text-sm">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}

      {/*
        Controlled, no `trigger` — both the form pencil-row button and the compact
        `CommandItem` open it via `handleRemoveSaved`. `itemName` reads the value
        captured before `handleConfirmRemove` clears the field on success, so the
        dialog's own closing transition never reads a null institution's name.
      */}
      <DeleteConfirmationDialog
        title="Remove institution"
        description="Remove {itemName} from your institution list?"
        itemName={
          currentInstitution && isSavedCustomInstitution(currentInstitution)
            ? currentInstitution.name
            : ''
        }
        open={removeConfirmOpen}
        onOpenChange={setRemoveConfirmOpen}
        onConfirm={handleConfirmRemove}
        destructiveWarning="This institution will no longer appear in your institution list or its pickers."
      />
    </div>
  );

  if (!compact) return body;

  /*
    **The compact field is a row, label column and all** — and that is what closed
    the one defect an isolated review of round 5 led with: an unlabelled full-bleed
    control was "the only row in the panel with no left column, so the section opens
    by contradicting the grid it then establishes two rows later".

    The label is back because the *reason* it was suppressed has gone. `hideLabel`
    was added when the section eyebrow above this field said `BANK` and the field
    said `Bank` a row later — the same word twice. The eyebrow now names the section
    (`ACCOUNT`, `FACILITY`) rather than the institution, so `Bank` is the field's
    name rather than an echo of the heading, and suppressing it costs the panel a
    column instead of saving it a repetition. `hideLabel` still does what it says —
    it just no longer has to be passed here.
  */
  /*
    **The label column has to be published here, or the row has no left column.**
    `ATTRIBUTE_LABEL` is `w-[var(--attr-label-w)]`, and an undefined custom property
    makes that width invalid — so the label falls back to its own text width and the
    institution starts ~90px left of every other value in the panel. That is what
    review saw as the picker being "a bit bugged (strange size or UI)": not the box,
    the column.

    `columns={2}` even though this field spans the drawer: the argument selects the
    *formula*, and 2 is the one the grid beside it resolves, so the two containers
    agree at every width. Published here rather than by the host so the field is
    correct standalone — the same reason `instruction-edit-section.tsx` publishes its
    own.
  */
  return (
    /*
      **The variable, but NOT a new `@container`** — and that distinction is a bug
      this walked into before it was written down.

      `ATTRIBUTE_LABEL_WIDTH` is a container query, so it is resolved against the
      nearest `@container` ancestor. When this field sits inside a two-up pair (the HIN
      form puts the broker beside the HIN), declaring `@container` here made that
      ancestor *this half-width cell* — so its label column came out roughly half the
      width of every other row's, and it was the first row on the panel. Publishing only
      the variable leaves the query resolving against whatever block-level container the
      host established, which is the one every other row is measured by.

      The variable is still set here rather than left to the host because
      `ATTRIBUTE_LABEL`'s `w-[var(--attr-label-w)]` is *invalid* when the property is
      undefined — the label then silently collapses to its own text width, which is a
      failure with no visible cause.
    */
    <div style={attributeLabelWidthStyle(2)}>
      <AttributeRow
        label={hideLabel ? null : institutionTypeName}
        required={required}
      >
        {body}
      </AttributeRow>
    </div>
  );
}
